import type { Notification } from '../shared/types.js';

/**
 * Release-style notifications collapse into a single inbox row. Both the
 * legacy `version_release` and the current `release_event` type are treated
 * as releases for grouping.
 */
export function isReleaseNotification(n: Notification): boolean {
  return n.type === 'version_release' || n.type === 'release_event';
}

/**
 * Entry the inbox renders for a row position. A `release-group` collapses
 * every non-dismissed release notification into one row so users who haven't
 * checked in for several releases see one summary instead of a stack of
 * near-duplicate entries.
 */
export type NotificationListEntry =
  | { kind: 'single'; notification: Notification }
  | {
      kind: 'release-group';
      /** Newest release (by `createdAt`) — drives the row's title/subtitle. */
      latest: Notification;
      /** Remaining grouped releases, newest first. */
      others: Notification[];
      /** Every grouped id; dismiss-all targets this list. */
      ids: string[];
    };

/**
 * Collapse two or more non-dismissed release notifications into a single
 * `release-group` entry positioned where the newest of them would sort in the
 * input list. A single non-dismissed release passes through as a regular
 * `single` entry — grouping a list of one would just add chrome for no
 * benefit. Dismissed releases always render individually so the Dismissed tab
 * still shows a real per-row history.
 */
export function groupReleaseNotifications(notifications: Notification[]): NotificationListEntry[] {
  const undismissedReleases = notifications.filter((n) => isReleaseNotification(n) && !n.dismissed);
  if (undismissedReleases.length < 2) {
    return notifications.map((n) => ({ kind: 'single', notification: n }));
  }
  const sortedReleases = [...undismissedReleases].sort((a, b) => b.createdAt - a.createdAt);
  const latest = sortedReleases[0];
  const others = sortedReleases.slice(1);
  const groupedIds = new Set(undismissedReleases.map((r) => r.id));

  const result: NotificationListEntry[] = [];
  let groupInserted = false;
  for (const n of notifications) {
    if (!groupedIds.has(n.id)) {
      result.push({ kind: 'single', notification: n });
      continue;
    }
    if (!groupInserted) {
      result.push({
        kind: 'release-group',
        latest,
        others,
        ids: [latest.id, ...others.map((o) => o.id)],
      });
      groupInserted = true;
    }
  }
  return result;
}
